/**
 * Shared creator-view sections — the SINGLE source of truth for the visual
 * language both the owner dashboard (OwnerDashboard) and the public
 * looked-up creator view (CreatorProfile) compose from. Change a section
 * here and both surfaces update together.
 *
 * Data honesty: every number is wired to real props; unknown values render
 * "\u2014" / are hidden. Owner-only affordances (edit buttons, save handlers)
 * are optional props — omit them and a section renders read-only.
 */
import React, { useState, useEffect, useRef, useId } from 'react';
import { relativeTime } from '../../utils/time';
import ChapterHeading from '../brand/ChapterHeading';
import { type DoodleName } from '../brand/Doodle';
import SectionSticker, { type StickerTone } from '../brand/SectionSticker';
import PrintSticker from '../brand/PrintSticker';
import type { BearriBadgeName } from '../brand/BearriBadge';
import './dashboard.css';
import './platformSlot.css';

// ── Shared data shapes (moved from UnifiedProfile) ──────────────────────
export type Tier = 'free' | 'plus' | 'pro' | 'studio';

export interface PlatformInfo {
  id: 'youtube' | 'instagram' | 'tiktok' | 'twitch';
  label: string;
  connected: boolean;
  saved?: boolean;
  sample?: boolean;
  connectable: boolean;
  /** How this platform connects: OAuth (YouTube/Instagram) or by entering
   *  the account @handle (TikTok/Twitch — no OAuth flow, but our scrape
   *  pipeline tracks any public account). Default 'oauth'. */
  connectVia?: 'oauth' | 'handle';
  followers: number | null;
  handle?: string | null;
}
export interface UploadItem {
  id: string; platform: string; title: string; thumbnail?: string | null;
  publishedAt?: string | null; url: string; scraped: boolean;
  brands: { name: string; logo?: string | null; color?: string | null }[];
}
export interface SponsorDeal {
  brand: string; logo?: string | null; color?: string | null; code?: string | null;
  offer?: string | null; category?: string | null; website?: string | null;
  videos: { title: string; thumbnail?: string | null; url?: string | null; likes?: number | null }[];
  /** Owner view only: the id of this sponsor's row in the codes table, when there is one. */
  codeId?: string | null;
  expires?: string | null;
}

/** Payload for the owner's "Add sponsor" editor. */
export interface AddSponsorInput {
  brand: string;
  code?: string | null;
  offer?: string | null;
  website?: string | null;
  brand_logo_url?: string | null;
  brand_color?: string | null;
  category?: string | null;
  videos?: { video_id: string; url?: string | null; title?: string | null; thumbnail_url?: string | null }[];
}

export const PLATFORM_ICON: Record<string, string> = {
  youtube: 'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
  instagram: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0 3.675c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4z',
  tiktok: 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z',
  twitch: 'M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z',
};
/** The same four inks as desk.css's global --platform-* tokens (the accessible pads chosen for
 *  the Search filters: YouTube's #ff0000 is 3.9:1 on paper, this red 4.8:1). An SVG fill
 *  attribute cannot read a custom property, so the values are repeated here — keep them equal. */
export const PLATFORM_COLOR: Record<string, string> = { youtube: '#e0161b', instagram: '#d62976', tiktok: '#111111', twitch: '#9146ff' };
export const PLATFORM_LABEL: Record<string, string> = { youtube: 'YouTube', instagram: 'Instagram', tiktok: 'TikTok', twitch: 'Twitch' };

export function compact(n: number): string {
  // Round within each unit, then promote if rounding tipped into the next
  // order of magnitude (e.g. 999,999 -> "1.0M", not "1000K").
  const unit = (v: number, div: number, suffix: string) => {
    const scaled = v / div;
    const digits = scaled >= 100 ? 0 : 1;
    const rounded = Number(scaled.toFixed(digits));
    return rounded >= 1000 ? null : `${rounded.toFixed(digits)}${suffix}`;
  };
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return unit(n, 1e6, 'M') ?? `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e3) return unit(n, 1e3, 'K') ?? `${(n / 1e6).toFixed(1)}M`;
  return n.toLocaleString();
}

/** <img> that falls back to `fallback` when src is missing or fails to load. */
export const FallbackImg: React.FC<{ src?: string | null; alt?: string; imgClassName?: string; fallback: React.ReactNode }> = ({ src, alt = '', imgClassName, fallback }) => {
  const [broken, setBroken] = useState(false);
  if (!src || broken) return <>{fallback}</>;
  return <img src={src} alt={alt} className={imgClassName} onError={() => setBroken(true)} />;
};
/** Re-exported under its historical name so OwnerDashboard and
 *  CreatorUnifiedSections keep their import path. The implementation is
 *  shared — see src/utils/time.ts. */
export const relDate = (iso?: string | null): string => relativeTime(iso);

/** Count-up on mount; respects reduced-motion; renders compact(target). */
export const CountUp: React.FC<{ value: number | null }> = ({ value }) => {
  const [display, setDisplay] = useState<number>(value ?? 0);
  const raf = useRef<number | null>(null);
  useEffect(() => {
    if (value == null) return;
    const reduce = typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || value < 1) { setDisplay(value); return; }
    const start = performance.now(); const dur = 900;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / dur);
      setDisplay(Math.round(value * (1 - Math.pow(1 - t, 3))));
      if (t < 1) raf.current = requestAnimationFrame(tick); else setDisplay(value);
    };
    raf.current = requestAnimationFrame(tick);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [value]);
  if (value == null) return <>—</>;
  return <>{compact(display)}</>;
};

export const PlatIcon: React.FC<{ id: string; size?: number; fill?: string }> = ({ id, size = 18, fill }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill || PLATFORM_COLOR[id]}><path d={PLATFORM_ICON[id]} /></svg>
);

// ── Section header ──────────────────────────────────────────────────────
/** One bear per desk: the hero owns the mascot, so sections pass no badge. A section of the
 *  dashboard or of a creator's profile names itself with a `doodle` instead: its engraving on
 *  a cardboard sticker beside the title (SectionSticker — tilts under the hand, spins on a
 *  click), in one of the starter-card `tone`s. Without a doodle it is a text heading over the
 *  rule, as before. A badge wins over a doodle: one piece of art per heading. */
export const SectionHeader: React.FC<{
  badge?: BearriBadgeName; busy?: boolean;
  title: string; subtitle?: string; right?: React.ReactNode; divider?: boolean;
  doodle?: DoodleName; tone?: StickerTone;
}> = ({ badge, busy, title, subtitle, right, divider = true, doodle, tone }) => {
  const sticker = Boolean(doodle) && !badge;
  const shell = [divider ? 'mt-9' : '', sticker ? 'br-chapter-shell--sticker' : ''].filter(Boolean).join(' ');
  return (
    <ChapterHeading className={shell} badge={badge} busy={busy}
      art={sticker ? <SectionSticker doodle={doodle!} tone={tone} label={title} /> : undefined}
      title={title} subtitle={subtitle} actions={right} />
  );
};

export const EditButton: React.FC<{ onClick?: () => void; label?: string }> = ({ onClick, label = 'Edit' }) => (
  <button type="button" onClick={onClick} className="br-slab br-slab--white br-slab--sm text-xs">{label}</button>
);

/** A white index card on the desk. */
export const Card: React.FC<{ children: React.ReactNode; className?: string; style?: React.CSSProperties }> = ({ children, className = '', style }) => (
  <div className={`br-sheet p-[18px] ${className}`} style={style}>{children}</div>
);

/** The heading written at the top of a sheet: a title, a line under it, and what to do there. */
export const SheetHeading: React.FC<{ title: string; sub?: string; right?: React.ReactNode }> = ({ title, sub, right }) => (
  <div className="mb-3.5 flex items-center justify-between gap-3">
    <div><h3 className="br-h3">{title}</h3>{sub && <p className="br-small mt-px">{sub}</p>}</div>
    {right}
  </div>
);

// A wash where ink will be — the loading state on any sheet.
export const Skeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`br-skeleton ${className}`} aria-hidden="true" />
);

// One skeleton platform tile / photo print / sponsor row, matched to the real ones.
export const PlatformSkeleton: React.FC = () => (
  <div className="br-sheet br-sheet--flat flex flex-col gap-2 p-[15px]">
    <div className="flex items-center gap-2.5">
      <Skeleton className="h-5 w-5" />
      <div className="flex-1 space-y-1.5"><Skeleton className="h-3 w-16" /><Skeleton className="h-2.5 w-20" /></div>
    </div>
    <Skeleton className="mt-1 h-6 w-24" />
  </div>
);

// ── The place of a platform that is not connected ───────────────────────
const LockGlyph: React.FC = () => (
  <svg className="br-platform-slot__lock" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
    <path d="M3.5 5.2V3.9a2.5 2.5 0 0 1 5 0v1.3" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    <rect x="2.2" y="5.2" width="7.6" height="5.4" rx="1" fill="currentColor" />
  </svg>
);

/**
 * Where a platform's sheet WOULD lie: a box the same size as a connected platform's sheet in
 * the same grid, drawn with a dashed line in the platform's own colour, its mark stamped in
 * colour in the middle and one line saying what it takes to fill it. Nothing in it is a figure:
 * an empty slot never shows a follower count, a "0", or a handle it does not have.
 *
 *   onClick   the whole box is ONE button (Connect TikTok / Find their TikTok)
 *   busy      the connection is on its way ("Connecting…"); further clicks are ignored
 *   locked    the viewer cannot link it (only the creator can): no button, a lock line
 *   children  replace the note inside the same box — the owner's inline @handle form.
 *             Lay a form out with `.br-platform-slot__form` (a line to write on + one slab).
 *
 * The box never changes size between its states: the resting face is always laid out, and
 * the form is laid over it in the same grid cell, so opening the form cannot shift the grid.
 * It stretches with its row (grid items stretch) and holds a connected sheet's height on its
 * own (`--br-platform-slot-min`), so a row of nothing but slots still lines up.
 */
export const PlatformSlot: React.FC<{
  id: PlatformInfo['id'] | string;
  headline: string;
  note?: React.ReactNode;
  onClick?: () => void;
  busy?: boolean;
  locked?: boolean;
  children?: React.ReactNode;
  className?: string;
}> = ({ id, headline, note, onClick, busy = false, locked = false, children, className = '' }) => {
  const noteId = useId();
  const label = PLATFORM_LABEL[id] || id;
  const open = children != null && children !== false;
  const interactive = Boolean(onClick) && !locked && !open;
  const state = locked ? 'locked' : open ? 'open' : busy ? 'busy' : interactive ? 'idle' : 'static';
  const line = busy && !locked ? 'Connecting…' : note ?? (locked ? 'Only the creator can link it' : null);
  const stamp = (small = false) => (
    <span className={`br-platform-slot__stamp${small ? ' br-platform-slot__stamp--sm' : ''}`} aria-hidden="true">
      <PlatIcon id={id} size={small ? 16 : 34} fill="currentColor" />
      {interactive && !small && <span className="br-platform-slot__plus" />}
    </span>
  );
  const face = (
    <span className="br-platform-slot__face" aria-hidden={open || undefined}>
      {stamp()}
      <span className="br-platform-slot__headline">{headline}</span>
      {(line || locked) && (
        <span id={noteId} className="br-platform-slot__note">
          {locked && <LockGlyph />}{line}
        </span>
      )}
    </span>
  );
  const common = {
    className: `br-platform-slot ${className}`.trim(),
    'data-platform': id,
    'data-state': state,
  };
  if (interactive) {
    // its name says the platform even when the headline does not ("Help link it")
    const named = headline.toLowerCase().includes(String(label).toLowerCase()) ? headline : `${headline} — ${label}`;
    return (
      <button type="button" {...common} aria-label={named} aria-describedby={line ? noteId : undefined}
        aria-disabled={busy || undefined} aria-busy={busy || undefined}
        onClick={() => { if (!busy) onClick!(); }}>
        {face}
      </button>
    );
  }
  return (
    <div {...common} aria-busy={busy || undefined}>
      {face}
      {open && (
        <div className="br-platform-slot__panel">
          <span className="br-platform-slot__panel-head">{stamp(true)}<span className="br-platform-slot__headline">{headline}</span></span>
          {children}
        </div>
      )}
    </div>
  );
};

export const UploadSkeleton: React.FC = () => (
  <div className="br-print">
    <Skeleton className="br-print__photo" />
    <div className="br-print__foot space-y-1.5"><Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-2/3" /></div>
  </div>
);
export const SponsorRowSkeleton: React.FC = () => (
  <div className="flex items-center gap-3.5 py-3.5">
    <Skeleton className="h-11 w-11" />
    <div className="flex-1 space-y-2"><Skeleton className="h-3.5 w-32" /><Skeleton className="h-3 w-48" /></div>
    <Skeleton className="h-8 w-14" />
  </div>
);

// ── The ledger: one column of the dashboard's figures sheet ─────────────
/** One column of a `.br-ledger` sheet: a label, a figure and a line under it.
 *  The parent renders the sheet (`<div className="br-sheet br-ledger">`) so
 *  three figures read as ONE object on the desk. The ledger carries no feet:
 *  the places to go are the folder tabs in the nav, not links under numbers. */
export const KpiColumn: React.FC<{
  label: string; value: number | null; sub: React.ReactNode; loading?: boolean;
}> = ({ label, value, sub, loading }) => (
  <div className="br-ledger__col">
    <div className="br-ledger__figure">
      <div className="br-ledger__label">{label}</div>
      <div className="br-ledger__number">
        {loading ? <Skeleton className="mt-1 h-8 w-20" /> : <CountUp value={value} />}
      </div>
      <div className="br-ledger__sub">{loading ? <Skeleton className="h-3 w-28" /> : sub}</div>
    </div>
  </div>
);

// ── Sponsor row (expandable) ────────────────────────────────────────────
/** Best-effort platform id from a content URL — for the badge on shared
 *  content tiles when the caller doesn't already know the platform. */
export function platformFromUrl(url?: string | null): string | null {
  if (!url) return null;
  const u = url.toLowerCase();
  if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
  if (u.includes('instagram.com')) return 'instagram';
  if (u.includes('tiktok.com')) return 'tiktok';
  if (u.includes('twitch.tv')) return 'twitch';
  return null;
}

/** Shared content-preview tile — a photo print: the thumbnail in a white
 *  border, thicker at the foot, with the detected brands written on the
 *  margin. The owner dashboard's Recent Uploads and the visitor profile's
 *  Sponsored Content strip render THIS, so the two surfaces can't drift. */
export const ContentTile: React.FC<{
  url?: string | null;
  title: string;
  thumbnail?: string | null;
  /** platform id for the corner badge; omit to derive from url. */
  platform?: string | null;
  brands: { name: string; color?: string | null }[];
  /** Shown when brands is empty (e.g. "No sponsors detected"). */
  footNote?: string;
}> = ({ url, title, thumbnail, platform, brands, footNote }) => {
  const plat = platform || platformFromUrl(url);
  const body = (
    <>
      <div className="br-print__photo">
        {/* the navy well is the print before its photo arrives; a failed load leaves it blank, not broken */}
        <FallbackImg src={thumbnail} fallback={null} />
        {plat && (
          <span className="br-print__platform" style={{ background: PLATFORM_COLOR[plat] }} title={PLATFORM_LABEL[plat] || plat}>
            <PlatIcon id={plat} size={12} fill="currentColor" />
          </span>
        )}
      </div>
      <div className="br-print__foot">
        <div className="br-print__title">{title}</div>
        <div className="br-print__brands">
          {brands.length > 0 ? (<>
            {brands.slice(0, 2).map((b, i) => <span key={i} className="br-print__brand">{b.name}</span>)}
            {brands.length > 2 && <span className="br-print__brand">+{brands.length - 2}</span>}
          </>) : (
            <span className="br-print__note">{footNote || ''}</span>
          )}
        </div>
      </div>
    </>
  );
  return url
    ? <a href={url} target="_blank" rel="noreferrer noopener" className="br-print">{body}</a>
    : <div className="br-print">{body}</div>;
};

export const SponsorRow: React.FC<{
  deal: SponsorDeal; open: boolean; onToggle: () => void;
  /** Optional per-deal action rendered as a footer bar (e.g. the visitor
   *  view's "Write Pitch" deep link). Owner dashboard omits it. */
  action?: React.ReactNode;
}> = ({ deal, open, onToggle, action }) => {
  const totalLikes = deal.videos.reduce((s, v) => s + (v.likes || 0), 0);
  const hasVideos = deal.videos.length > 0;
  // Clicking the brand goes to its site (the domain we already know from the
  // favicon logo). stopPropagation so it doesn't also toggle the video list.
  const brandDomainLabel = deal.website
    ? deal.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')
    : null;
  const headerContent = (
    <>
      {/* the brand's mark on a sticker. A click on it is the row's (it opens the videos); the brand's
          site is the name's link, so the mark is no longer a second tab stop to the same place */}
      <PrintSticker name={deal.brand} src={deal.logo} mark size="sm" interactive={hasVideos} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          {deal.website ? (
            <a href={deal.website} target="_blank" rel="noreferrer noopener"
              onClick={e => e.stopPropagation()}
              title={`Visit ${deal.brand} — ${brandDomainLabel}`}
              className="br-h3 hover:underline">
              {deal.brand}
            </a>
          ) : (
            <span className="br-h3">{deal.brand}</span>
          )}
          {deal.code && <span className="br-mono">{deal.code}</span>}
        </div>
        {deal.offer && <p className="br-small mt-1 truncate">{deal.offer}</p>}
      </div>
      <div className="flex flex-shrink-0 items-center gap-[22px]">
        {totalLikes > 0 && (
          <div className="text-right"><div className="br-num text-[15px]">{compact(totalLikes)}</div><div className="br-caption">likes</div></div>
        )}
        {/* the column keeps its width; the pen's line is only as long as the word */}
        {hasVideos && (
          <span className="inline-flex min-w-[92px] justify-end">
            <span className="br-inkline whitespace-nowrap text-xs">{open ? 'Hide' : 'Videos'}</span>
          </span>
        )}
      </div>
    </>
  );
  // A ruled row on the sponsors sheet: the parent list is `br-ruled`, so the
  // dashed rule between rows is drawn there, not here.
  return (
    <div>
      {hasVideos ? (
        // role=button (not a real <button>) so the brand-name/logo anchors
        // above can nest validly; keyboard-accessible via tabIndex + keydown.
        <div role="button" tabIndex={0} aria-expanded={open} onClick={onToggle}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
          className="flex w-full cursor-pointer items-center gap-3.5 py-3.5 text-left">
          {headerContent}
        </div>
      ) : (
        <div className="flex items-center gap-3.5 py-3.5">{headerContent}</div>
      )}
      {action}
      {open && hasVideos && (
        <div className="br-sheet br-sheet--cream br-sheet--plain mb-3.5 p-4">
          <div className="br-tab br-tab--white br-tab--sm br-tab--plain mb-2">Featured in {deal.videos.length} {deal.videos.length === 1 ? 'video' : 'videos'}</div>
          <div className="br-ruled">
            {deal.videos.slice(0, 6).map((v, i) => {
              const vplat = platformFromUrl(v.url);
              const inner = (
                <>
                  <span className="br-print__photo h-9 w-16 flex-shrink-0">
                    <FallbackImg src={v.thumbnail} fallback={null} />
                    {vplat && (
                      <span className="br-print__platform br-print__platform--sm" style={{ background: PLATFORM_COLOR[vplat] }} title={PLATFORM_LABEL[vplat] || vplat}>
                        <PlatIcon id={vplat} size={10} fill="currentColor" />
                      </span>
                    )}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-xs font-semibold text-text-primary">{v.title || 'Untitled'}</span>
                  {typeof v.likes === 'number' && v.likes > 0 && (
                    <span className="br-caption tabular-nums"><strong className="br-num text-text-primary">{compact(v.likes)}</strong> likes</span>
                  )}
                </>
              );
              return v.url
                ? <a key={i} href={v.url} target="_blank" rel="noreferrer noopener" className="flex items-center gap-2.5 py-2 hover:underline">{inner}</a>
                : <div key={i} className="flex items-center gap-2.5 py-2">{inner}</div>;
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/** Recover a brand's site from a Google-favicon logo URL — the favicon
 *  service embeds the resolved domain (?url=https://DOMAIN), so a brand we
 *  have a logo for also tells us its website even when the API row's
 *  brand_website is empty (legacy rows). Mirrors the backend
 *  website_from_logo_url; lets the sponsor card link out immediately without
 *  waiting on a data backfill. */
export function websiteFromLogoUrl(logoUrl?: string | null): string | null {
  if (!logoUrl) return null;
  const m = logoUrl.match(/[?&]url=https?:\/\/([^&]+)/);
  if (!m) return null;
  const domain = m[1].replace(/\/$/, '').trim();
  if (!domain || domain.includes(' ') || !domain.includes('.')) return null;
  return `https://${domain}`;
}

// ── Shared transform: API sponsorships → dashboard deals ────────────────
// One grouping implementation for BOTH surfaces (owner dashboard + public
// creator view), so the brand/code/website/video mapping can never drift.
export interface SponsorshipLike {
  brand_name: string;
  brand_logo_url?: string | null;
  brand_color?: string | null;
  brand_website?: string | null;
  value?: string | null;
  offer_text?: string | null;
  category?: string | null;
  content_sources?: {
    content_id?: string | null; title?: string | null; thumbnail_url?: string | null;
    url?: string | null; likes_count?: number | null; published_at?: string | null;
  }[] | null;
}

export function buildDealsAndContentIndex(sponsorships: SponsorshipLike[]): {
  deals: SponsorDeal[];
  brandsByContentId: Map<string, { name: string; logo?: string | null; color?: string | null }[]>;
} {
  const byBrand = new Map<string, SponsorDeal>();
  const brandsByContentId = new Map<string, { name: string; logo?: string | null; color?: string | null }[]>();
  for (const sp of sponsorships || []) {
    const isUrl = /^https?:/i.test(sp.value || '');
    const key = sp.brand_name;
    if (!byBrand.has(key)) {
      byBrand.set(key, {
        brand: sp.brand_name, logo: sp.brand_logo_url, color: sp.brand_color,
        code: isUrl ? null : (sp.value || null), offer: sp.offer_text || null,
        category: sp.category || null,
        website: sp.brand_website || websiteFromLogoUrl(sp.brand_logo_url) || (isUrl ? sp.value : null) || null,
        videos: [],
      });
    }
    const deal = byBrand.get(key)!;
    for (const cs of sp.content_sources || []) {
      deal.videos.push({ title: cs.title || '', thumbnail: cs.thumbnail_url, url: cs.url, likes: cs.likes_count ?? null });
      if (cs.content_id) {
        const arr = brandsByContentId.get(cs.content_id) || [];
        if (!arr.some(b => b.name === sp.brand_name)) arr.push({ name: sp.brand_name, logo: sp.brand_logo_url, color: sp.brand_color });
        brandsByContentId.set(cs.content_id, arr);
      }
    }
  }
  return { deals: Array.from(byBrand.values()), brandsByContentId };
}

/** The fields of a codes-table row the owner's sponsor list reads (services/codeService `Code`). */
export interface CodeLike {
  id?: string; code: string; brand: string; store?: string | null; discount?: string | null;
  category?: string | null; logo?: string | null; iconUrl?: string | null; brandColor?: string | null;
  expirationDate?: string | null;
  sources?: { title?: string | null; url?: string | null; thumbnailUrl?: string | null }[] | null;
}

const DOMAIN_RE = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;
const meaningful = (s?: string | null) => { const t = (s || '').trim(); return t && t.toUpperCase() !== 'N/A' ? t : null; };

/**
 * The owner's ONE sponsor list. A sponsor used to be filed in two places that never agreed:
 * the sponsorships detected in (or added to) the creator's videos, and the codes table — a
 * code added on the old Sponsors page never reached the dashboard. This
 * reads both. A code joins the sponsorship that carries it (same code, any case), else a
 * sponsorship of the same brand that has no code yet, else it becomes a row of its own.
 * Nothing is inferred: a code row shows only what the code row says.
 */
export function mergeSponsorCodes(deals: SponsorDeal[], codes: CodeLike[] | null): SponsorDeal[] {
  if (!codes || !codes.length) return deals;
  const out = deals.map(d => ({ ...d }));
  const byCode = new Map<string, SponsorDeal>();
  for (const d of out) if (d.code) byCode.set(d.code.toLowerCase(), d);
  for (const c of codes) {
    const value = (c.code || '').trim();
    if (!value) continue;
    const isUrl = /^https?:/i.test(value);
    const match = !isUrl ? byCode.get(value.toLowerCase()) : undefined;
    if (match) {
      match.codeId = match.codeId || c.id || null;
      match.expires = match.expires || c.expirationDate || null;
      continue;
    }
    const brand = (c.brand || '').trim();
    const sameBrand = !isUrl && brand
      ? out.find(d => !d.code && d.brand.trim().toLowerCase() === brand.toLowerCase())
      : undefined;
    if (sameBrand) {
      Object.assign(sameBrand, { code: value, codeId: c.id || null, expires: c.expirationDate || null,
        offer: sameBrand.offer || meaningful(c.discount) });
      byCode.set(value.toLowerCase(), sameBrand);
      continue;
    }
    const store = (c.store || '').trim();
    const row: SponsorDeal = {
      brand: brand || 'Unknown brand',
      logo: c.logo || c.iconUrl || null,
      color: c.brandColor || null,
      code: isUrl ? null : value,
      offer: meaningful(c.discount),
      category: c.category && c.category !== 'Other' ? c.category : null,
      website: isUrl ? value : DOMAIN_RE.test(store) ? `https://${store.toLowerCase()}` : null,
      videos: (c.sources || []).filter(s => s && (s.title || s.url))
        .map(s => ({ title: s.title || '', thumbnail: s.thumbnailUrl || null, url: s.url || null })),
      codeId: c.id || null,
      expires: c.expirationDate || null,
    };
    out.push(row);
    if (row.code) byCode.set(row.code.toLowerCase(), row);
  }
  return out;
}
